import {
  Box,
  Grid,
  makeStyles,
  Paper,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
} from "@material-ui/core"
import React, { useState, useEffect } from "react"
import ReportFilters from "./report-filters"
import FilterListIcon from "@material-ui/icons/FilterList"
import RefreshIcon from "@material-ui/icons/Refresh"
import GetAppIcon from "@material-ui/icons/GetApp" // Add this for download icons
import FileCopyIcon from "@material-ui/icons/FileCopy"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import useStore from "../../utils/store"
import { useAlertStore } from "src/v2/utils/alert-store" // Add this for alerts
import CustomModal from "../../components/ui-components/custom-modal"
import CustomCheckbox from "../form-components/custom-checkbox"
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined"

// Category id used by the backend to tag findings that come from failed AI
// simulation runs. Reports with this category are surfaced under the
// "Simulation Test Failures" tab instead of the main "Security Findings" tab.
const SIMULATION_CATEGORY_ID = "simulation-test-failures"
const isSimulationReport = (report) => {
  if (!report) return false
  if (Array.isArray(report.categories)) {
    return report.categories.includes(SIMULATION_CATEGORY_ID)
  }
  return report.category === SIMULATION_CATEGORY_ID
}

// @ts-ignore
import { useParams } from "react-router-dom"
import { getEndPoint } from "src/utils/endpoint"

import { Document, Paragraph, TextRun, HeadingLevel, BorderStyle, ShadingType, Packer } from 'docx';
import  saveAs  from 'file-saver';
import ExcelJS from 'exceljs';
import reportFiltersData from "../../utils/reportFilters.json"

const useStyles = makeStyles((theme) => ({
  reportBox: {
    marginBottom: theme.spacing(1),
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: theme.spacing(1.25, 1.5),
  },
  reportTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },
  sectionHeading: {
    fontSize: "0.9rem",
    fontWeight: 600,
    lineHeight: 1.3,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
  },
  capsuleRow: {
    marginBottom: theme.spacing(0.5),
  },
  capsule: {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: "12px",
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
    backgroundColor: "#fff",
    border: "1px solid",
    fontSize: "0.72rem",
    lineHeight: 1.4,
  },
  critical: {
    color: "#ff0000",
    borderColor: "#ff0000",
  },
  high: {
    color: "#f69e08",
    borderColor: "#f69e08",
  },
  medium: {
    color: "#d1b900",
    borderColor: "#d1b900",
  },
  low: {
    color: "#0bc543",
    borderColor: "#0bc543",
  },
  informational: {
    color: "#757575",
    borderColor: "#757575",
  },
  category: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
  },
  filterHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  filterTitleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  description: {
    marginBottom: theme.spacing(0.75),
    marginTop: theme.spacing(0.25),
    fontSize: "0.85rem",
    lineHeight: 1.45,
  },
  recommendation: {
    marginTop: theme.spacing(0.25),
    fontSize: "0.85rem",
    lineHeight: 1.45,
  },
  linkContainer: {
    display: "flex",
    justifyContent: "space-between", // Changed from flex-end to space-between
    alignItems: "center", // Added to vertically align items
    marginBottom: theme.spacing(2),
  },
  link: {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(2),
    cursor: "pointer",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  downloadIcon: {
    fontSize: "0.9rem",
    marginRight: theme.spacing(0.5),
  },
  downloadingSpinner: {
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  runningTests: {
    marginLeft: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
  },
  reRunButton: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.primary,
    cursor: "pointer",
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      textDecoration: "underline",
    },
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: theme.shape.borderRadius,
  },
  progressText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  recommendationContainer: {
    backgroundColor: "#f6f8fa",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.25),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  recommendationHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.25),
  },
  recommendationIcon: {
    color: "#f6bc41",
    marginRight: theme.spacing(0.75),
    fontSize: "1.1rem",
  },
  recommendationTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#24292e",
  },
  recommendationContent: {
    color: "#57606a",
    fontSize: "0.85rem",
    lineHeight: 1.45,
    marginLeft: theme.spacing(2.75), // Align with text after icon
  },
}))

export default function FindingsAndRecommendations() {
  const classes = useStyles()
  const { reviewId } = useParams()
  const oneReview = useStore((state) => state.oneReview)
  const fetchOneReview = useStore((state) => state.fetchOneReview)
  const reRunTests = useStore((state) => state.reRunTests)
  const reRunAllSimulations = useStore((state) => state.reRunAllSimulations)
  const fetchSimulations = useStore((state) => state.fetchSimulations)
  const saveReportsToReview = useStore((state) => state.saveReportsToReview)
  const generateSimulationFailureReports = useStore((state) => state.generateSimulationFailureReports)
  const createLog = useStore((state) => state.createLog)
  const showAlert = useAlertStore((s) => s.showAlert)
  const [isRunningTests, setIsRunningTests] = useState(false)
  const [reports, setReports] = useState([])

  // Add state for tracking the re-run process
  const [reRunProgress, setReRunProgress] = useState({
    isRunning: false,
    currentStep: '',
    stepIndex: 0,
    totalSteps: 4,
    simulationsSkipped: false // Track if simulations were skipped
  })

  // Add state for downloading status
  const [isDownloading, setIsDownloading] = useState({
    pdf: false,
    docx: false,
    csv: false,
    xlsx: false,
    json: false,
    api: false,
  })

  // Tab state: 0 = Security Findings, 1 = Simulation Test Failures.
  const [activeTab, setActiveTab] = useState(0)

  // Modal that asks the user whether the export should include simulation
  // test failure findings. Only opened when simulation findings exist.
  const [downloadModal, setDownloadModal] = useState({
    open: false,
    format: null,            // 'pdf' | 'docx' | 'csv' | 'xlsx' | 'json'
    includeSimulations: true // default: include
  })

  // New state for dialog visibility
  const [apiDialogOpen, setApiDialogOpen] = useState(false)
  const [apiInfo, setApiInfo] = useState({ endpoint: "", token: "" })

  // Update local reports state when oneReview changes
  useEffect(() => {
    if (oneReview && oneReview.reports) {
      setReports(oneReview.reports)
    } else {
      setReports([])
    }
  }, [oneReview])

  const showReRunButton = window["config"]?.DEBUG_SHOW_RE_RUN === "YES"
  // const showReRunButton = true
  // Function to handle re-running tests with simulation processing and report generation
  const handleReRunTests = async () => {
    if (isRunningTests || reRunProgress.isRunning) return

    setIsRunningTests(true)
    setReRunProgress({
      isRunning: true,
      currentStep: 'Running tests',
      stepIndex: 1,
      totalSteps: 4,
      simulationsSkipped: false
    })

    createLog({
      event_type: 'Review.ReRunTests',
      operation: 'Re-run Tests',
      review_id: reviewId,
    })

    try {
      const testResults = await reRunTests(reviewId, showAlert)
      
      if (!testResults) {
        throw new Error("Failed to run tests")
      }

      // Step 2: Running Simulations
      setReRunProgress({
        isRunning: true,
        currentStep: 'Running simulations',
        stepIndex: 2,
        totalSteps: 4,
        simulationsSkipped: false
      })

      try {
        const simulationResult = await reRunAllSimulations(reviewId, showAlert)
        
        if (simulationResult && simulationResult.success) {
          await new Promise(resolve => setTimeout(resolve, 1000))
          await fetchSimulations(reviewId)
        }
      } catch (err) {
        console.error("Error running simulations:", err)
      }

      // Step 3: Generating Reports
      setReRunProgress({
        isRunning: true,
        currentStep: 'Generating reports',
        stepIndex: 3,
        totalSteps: 4,
        simulationsSkipped: false
      })

      const allReports = []
      
      try {
        const reportsResult = await generateSimulationFailureReports(reviewId, showAlert)
        
        if (reportsResult && reportsResult.success && reportsResult.reports && Array.isArray(reportsResult.reports)) {
          allReports.push(...reportsResult.reports)
        }
      } catch (reportGenErr) {
        console.error('Error generating simulation failure reports:', reportGenErr)
      }

      if (testResults && testResults.results) {
        testResults.results.forEach((test) => {
          if (test.issues && test.issues.length > 0) {
            test.issues.forEach((issue) => {
              const report = {
                title: issue.title,
                severity: issue.severity ? issue.severity.toLowerCase() : "medium",
                categories: issue.labels || [],
                description: issue.description || "",
                recommendation: issue.recommendation || "",
                potentialRisk: issue.potentialRisk || "",
                filePath: issue.filePath || "",
                codeSnippet: issue.codeSnippet || "",
                lineNumber: issue.lineNumber,
              }
              allReports.push(report)
            })
          }
        })
      }

      if (allReports.length > 0) {
        const saveResponse = await saveReportsToReview(allReports, reviewId, showAlert)

        if (!saveResponse || !saveResponse.success) {
          console.error("Failed to save reports to review:", saveResponse)
          showAlert({
            message: `Warning: Failed to save ${allReports.length} reports to review`,
            severity: "warning",
            title: "Warning",
          })
        }
      }

      // Step 4: Refreshing Data
      setReRunProgress({
        isRunning: true,
        currentStep: 'Refreshing data',
        stepIndex: 4,
        totalSteps: 4,
        simulationsSkipped: false
      })

      await fetchOneReview(reviewId)

      // Show success message based on what was actually generated
      const simulationReportCount = allReports.filter(r => r.categories && r.categories.includes('simulation-test-failures')).length
      const testReportCount = allReports.length - simulationReportCount
      
      if (allReports.length > 0) {
        let message = `Re-run completed successfully! Generated ${allReports.length} report(s)`
        if (simulationReportCount > 0 && testReportCount > 0) {
          message += ` (${simulationReportCount} simulation + ${testReportCount} test issue reports)`
        } else if (simulationReportCount > 0) {
          message += ` (${simulationReportCount} simulation failure reports with AI analysis)`
        } else {
          message += ` (${testReportCount} test issue reports)`
        }
        
        showAlert({
          message,
          severity: "success",
          title: "Success",
        })
      } else {
        showAlert({
          message: "Re-run completed successfully! No new reports generated.",
          severity: "success", 
          title: "Success",
        })
      }

    } catch (error) {
      console.error("Error during re-run process:", error)
      createLog({
        event_type: 'Review.ReRunTests',
        operation: 'Re-run Tests',
        review_id: reviewId,
        result: 'failure',
        http_status: 500,
        error_message: error && error.message,
      })
      showAlert({
        message: error.message || "Failed to re-run tests and simulations",
        severity: "error",
        title: "Error",
      })
    } finally {
      setIsRunningTests(false)
      setReRunProgress({
        isRunning: false,
        currentStep: '',
        stepIndex: 0,
        totalSteps: 4,
        simulationsSkipped: false
      })
    }
  }

  const isDesktop = useMediaQuery("(min-width:960px)") // Check if the screen is desktop size
  const [filtersVisible, setFiltersVisible] = useState(isDesktop) // Set initial visibility based on screen size

  const [filters] = useState(reportFiltersData)

  const [checkedFilters, setCheckedFilters] = useState(() => {
    const initialChecked = {}
    const checkAll = (filters) => {
      filters.forEach((filter) => {
        initialChecked[filter.id] = true
        if (filter.children) {
          checkAll(filter.children)
        }
      })
    }
    checkAll(filters)
    return initialChecked
  })

  const [expanded, setExpanded] = useState(() => {
    const initialExpanded = {}
    const expandAll = (filters) => {
      filters.forEach((filter) => {
        initialExpanded[filter.id] = true
        if (filter.children) {
          expandAll(filter.children)
        }
      })
    }
    expandAll(filters)
    return initialExpanded
  })

  const handleFilterChange = (filter) => {
    const newCheckedFilters = { ...checkedFilters }
    const isChecked = !!newCheckedFilters[filter.id]

    const toggleCheck = (filter, check) => {
      if (check) {
        newCheckedFilters[filter.id] = true
      } else {
        delete newCheckedFilters[filter.id]
      }
      if (filter.children) {
        filter.children.forEach((child) => toggleCheck(child, check))
      }
    }

    toggleCheck(filter, !isChecked)
    setCheckedFilters(newCheckedFilters)
  }

  const handleExpandToggle = (filterId) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [filterId]: !prevExpanded[filterId],
    }))
  }

  const handleClearAll = () => {
    setCheckedFilters({}) // This empties the checkedFilters object, clearing all selections
  }

  const toggleFiltersVisibility = () => {
    if (!isDesktop) {
      setFiltersVisible(!filtersVisible)
    }
  }

  // Find selected severities
  const severityFilter = filters.find((filter) => filter.id === "severity")
  const allSeverityIds = (severityFilter?.children || []).map((c) => c.id)
  const selectedSeverities = (severityFilter?.children || [])
    .filter((child) => checkedFilters[child.id])
    .map((child) => child.id)

  // When every severity checkbox is checked (the default), treat the severity
  // filter as inactive so findings with missing or unrecognized severity
  // values are not hidden.
  const allSeveritiesSelected =
    allSeverityIds.length > 0 &&
    allSeverityIds.every((id) => checkedFilters[id])

  // Find selected categories (from all non-severity filters)
  const categoryFilters = filters.filter((filter) => filter.id !== "severity")

  // Collect every known category id across all non-severity filter groups
  // (including nested children). Used to detect the "everything checked"
  // default state, in which case category filtering is bypassed entirely so
  // that findings tagged with categories not present in reportFilters.json
  // are still displayed.
  const collectCategoryIds = (nodes) => {
    const ids = []
    const walk = (list) => {
      list.forEach((node) => {
        ids.push(node.id)
        if (node.children) walk(node.children)
      })
    }
    nodes.forEach((group) => {
      if (group.children) walk(group.children)
    })
    return ids
  }
  const allCategoryIds = collectCategoryIds(categoryFilters)

  const selectedCategories = categoryFilters
    .map((filter) =>
      filter.children
        ? filter.children
            .filter((child) => checkedFilters[child.id])
            .map((child) => child.id)
        : [],
    )
    .reduce((acc, val) => acc.concat(val), [])

  // When every known category checkbox is checked (the default), treat the
  // category filter as inactive so findings with unrecognized category tags
  // are not hidden.
  const allCategoriesSelected =
    allCategoryIds.length > 0 &&
    allCategoryIds.every((id) => checkedFilters[id])

  // Severity-rank used for sorting reports inside each tab.
  const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 }
  const sortBySeverity = (list) =>
    [...list].sort((a, b) => {
      const ra = SEVERITY_RANK[(a.severity || "").toLowerCase()]
      const rb = SEVERITY_RANK[(b.severity || "").toLowerCase()]
      const va = ra === undefined ? 99 : ra
      const vb = rb === undefined ? 99 : rb
      return va - vb
    })

  const passesSeverityFilter = (report) => {
    if (allSeveritiesSelected) return true
    if (selectedSeverities.length === 0) return true
    const reportSeverity = (report.severity || "").toLowerCase()
    return selectedSeverities.some((sev) => sev.toLowerCase() === reportSeverity)
  }

  const passesCategoryFilter = (report) => {
    if (allCategoriesSelected) return true
    if (selectedCategories.length === 0) return true
    if (Array.isArray(report.categories)) {
      return report.categories.some((category) =>
        selectedCategories.includes(category),
      )
    }
    if (report.category) {
      return selectedCategories.includes(report.category)
    }
    return false
  }

  // Pre-split the raw reports so the two tabs can be filtered independently.
  // Simulation findings are tagged with `simulation-test-failures` (which is
  // intentionally NOT in the filter sidebar) so they must bypass the category
  // filter — otherwise selecting any category in the sidebar would hide them.
  const rawSecurityReports = (reports || []).filter((r) => !isSimulationReport(r))
  const rawSimulationReports = (reports || []).filter(isSimulationReport)

  // Filtering only kicks in when the user has actively touched the filters.
  // This preserves the original behavior where an empty selection means
  // "show nothing" for the security tab.
  const anyFilterSelected =
    selectedSeverities.length > 0 || selectedCategories.length > 0

  const securityReports = anyFilterSelected
    ? sortBySeverity(
        rawSecurityReports.filter(
          (r) => passesSeverityFilter(r) && passesCategoryFilter(r),
        ),
      )
    : []

  // Simulation findings get severity filtering only (category filter does not
  // apply to them). When no filters are selected we still show all simulation
  // findings so the tab is useful out of the box.
  const simulationReports = sortBySeverity(
    rawSimulationReports.filter(passesSeverityFilter),
  )

  // Keep `filteredReports` available for any legacy consumers (e.g. exports
  // that bundle both tabs together).
  const filteredReports = [...securityReports, ...simulationReports]

  // Simulation tab visibility is driven by the raw reports (ignoring filters)
  // so the tab does not disappear when the user filters everything out.
  const hasSimulationReports = rawSimulationReports.length > 0

  // Make sure the active tab is valid (e.g. user switches away from a tab that
  // has just disappeared after a re-run).
  useEffect(() => {
    if (!hasSimulationReports && activeTab === 1) {
      setActiveTab(0)
    }
  }, [hasSimulationReports, activeTab])

  // Reports actually shown in the current tab.
  const displayedReports = activeTab === 1 ? simulationReports : securityReports

  // Helper function to find category label by ID
  const findCategoryLabel = (categoryId) => {
    for (const filter of categoryFilters) {
      for (const child of filter.children || []) {
        if (child.id === categoryId) {
          return { label: child.label, filter: filter }
        }
        if (child.children) {
          for (const grandChild of child.children) {
            if (grandChild.id === categoryId) {
              return { label: grandChild.label, filter: filter }
            }
          }
        }
      }
    }
    return { label: categoryId, filter: { id: "unknown" } }
  }

  // Add these download handler functions

  // Record a Report.Download audit event. Fire-and-forget — never blocks
  // the download and never surfaces failures to the user.
  const logReportDownload = (format, reportsToExport) => {
    try {
      createLog({
        event_type: `Report.Download.${String(format).toUpperCase()}`,
        operation: `Download Report (${String(format).toUpperCase()})`,
        review_id: oneReview?._id,
        metadata: {
          format,
          totalReports: Array.isArray(reportsToExport) ? reportsToExport.length : 0,
        },
      })
    } catch (_) { /* never let logging break download */ }
  }

  // PDF download handler
  const handleDownloadAsPdf = async (reportsToExport = filteredReports) => {
    if (isDownloading.pdf || !oneReview?._id) return

    setIsDownloading((prev) => ({ ...prev, pdf: true }))
    logReportDownload('pdf', reportsToExport)

    try {
      const reviewName = oneReview?.name || "findings"
      const filename = `${reviewName.replace(/\s+/g, "-")}-findings.pdf`

      const printWindow = window.open("", "_blank")

      if (!printWindow) {
        throw new Error("Pop-up blocked. Please allow pop-ups for this site.")
      }

      // Create a formatted HTML document for the reports
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${filename}</title>
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
                color: #333;
                line-height: 1.35;
                margin: 10px;
                padding: 0;
                font-size: 10pt;
              }
              h1 { color: #2c3e50; margin: 8px 0; font-size: 15pt; }
              h2 { color: #2980b9; margin: 6px 0 4px; font-size: 12pt; }
              h3 { color: #3498db; margin: 6px 0 3px; font-size: 11pt; }
              h4 { margin: 4px 0 2px; font-size: 9.5pt; }
              p { margin: 2px 0; }
              .report {
                border: 1px solid #ddd;
                border-radius: 4px;
                padding: 6px 8px;
                margin-bottom: 8px;
                page-break-inside: avoid;
              }
              .capsule {
                display: inline-block;
                padding: 1px 6px;
                border-radius: 10px;
                margin-right: 4px;
                margin-bottom: 3px;
                font-size: 0.72em;
                font-weight: bold;
                border: 1px solid;
              }
              .critical {
                color: #ff0000;
                border-color: #ff0000;
              }
              .high {
                color: #f69e08;
                border-color: #f69e08;
              }
              .medium {
                color: #d1b900;
                border-color: #d1b900;
              }
              .low {
                color: #0bc543;
                border-color: #0bc543;
              }
              .informational {
                color: #757575;
                border-color: #757575;
              }
              .category {
                color: #1a00ff;
                border-color: #1a00ff;
              }
              .description, .recommendation, .risk {
                margin-bottom: 4px;
              }
              .code-snippet {
                background-color: #f5f5f5;
                padding: 5px 6px;
                font-family: monospace;
                font-size: 9pt;
                border: 1px solid #ddd;
                border-radius: 4px;
                margin-top: 3px;
                white-space: pre-wrap;
              }
              .file-info {
                font-weight: bold;
                font-size: 9pt;
                margin-bottom: 2px;
              }
              @media print {
                body { font-size: 9pt; }
                h1 { font-size: 14pt; }
                h2 { font-size: 11pt; }
                h3 { font-size: 10.5pt; }
                .report { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <h1>Findings and Recommendations</h1>
            <h2>Review: ${oneReview?.name || "Smart Contract Review"}</h2>
            
            ${reportsToExport
              .map(
                (report, index) => `
              <div class="report">
                <h3>${report.title}</h3>
                <div>
                  <span class="capsule ${report.severity?.toLowerCase()}">${
                  report.severity?.toUpperCase() || "MEDIUM"
                }</span>
                  ${
                    Array.isArray(report.categories)
                      ? report.categories
                          .map(
                            (category) =>
                              `<span class="capsule category">${
                                findCategoryLabel(category).label
                              }</span>`,
                          )
                          .join("")
                      : report.category
                      ? `<span class="capsule category">${
                          findCategoryLabel(report.category).label
                        }</span>`
                      : ""
                  }
                </div>
                ${
                  report.description && String(report.description).trim()
                    ? `
                <div class="description">
                  <h4>Description</h4>
                  <p>${report.description}</p>
                </div>`
                    : ""
                }
                ${
                  report.potentialRisk && String(report.potentialRisk).trim()
                    ? `
                <div class="risk">
                  <h4>Potential Risk</h4>
                  <p>${report.potentialRisk}</p>
                </div>`
                    : ""
                }
                <div class="recommendation">
                  <h4>Recommendation</h4>
                  <p>${report.recommendation}</p>
                </div>
                ${
                  report.codeSnippet && report.codeSnippet.trim()
                    ? `
                <div>
                  <h4>Code Snippet</h4>
                  <div class="file-info">${report.filePath}${
                        report.lineNumber
                          ? ` line ${report.lineNumber}`
                          : ""
                      }:</div>
                  <div class="code-snippet">${report.codeSnippet.trim()}</div>
                </div>`
                    : ""
                }
              </div>
            `,
              )
              .join("")}
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 1000);
              };
            </script>
          </body>
        </html>
      `)

      printWindow.document.close()

      showAlert({
        message:
          "Print dialog opened in a new tab. Save as PDF to download the document.",
        severity: "info",
        title: "Print to PDF",
      })
    } catch (error) {
      console.error("Error generating PDF:", error)
      showAlert({
        message:
          error.message ||
          "Could not generate PDF. Please try printing directly.",
        severity: "error",
        title: "PDF Error",
      })
    } finally {
      setIsDownloading((prev) => ({ ...prev, pdf: false }))
    }
  }

  // JSON download handler
  const handleDownloadAsJson = (reportsToExport = filteredReports) => {
    if (isDownloading.json || !oneReview?._id) return

    setIsDownloading((prev) => ({ ...prev, json: true }))
    logReportDownload('json', reportsToExport)

    try {
      const reviewName = oneReview?.name || "findings"
      const filename = `${reviewName.replace(/\s+/g, "-")}-findings.json`

      // Create a formatted JSON object with the reports
      const jsonData = {
        reviewName: oneReview?.name || "Smart Contract Review",
        reviewId: oneReview?._id,
        generatedAt: new Date().toISOString(),
        findings: reportsToExport.map((report) => ({
          title: report.title,
          severity: report.severity,
          categories: Array.isArray(report.categories)
            ? report.categories
            : report.category
            ? [report.category]
            : [],
          description: report.description,
          potentialRisk: report.potentialRisk,
          recommendation: report.recommendation,
          codeReference: {
            filePath: report.filePath,
            lineNumber: report.lineNumber,
            codeSnippet: report.codeSnippet?.trim(),
          },
        })),
      }

      // Create a Blob with the JSON data
      const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], {
        type: "application/json",
      })
      const url = URL.createObjectURL(jsonBlob)

      // Create a temporary link and trigger the download
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }, 100)

      showAlert({
        message: `JSON file "${filename}" is being downloaded.`,
        severity: "success",
        title: "JSON Download",
      })
    } catch (error) {
      console.error("Error generating JSON:", error)
      showAlert({
        message: error.message || "Could not generate JSON file.",
        severity: "error",
        title: "JSON Error",
      })
    } finally {
      setIsDownloading((prev) => ({ ...prev, json: false }))
    }
  }

  // XLSX download handler (true Excel format)
  const handleDownloadAsXlsx = async (reportsToExport = filteredReports) => {
    if (isDownloading.xlsx || !oneReview?._id) return;

    setIsDownloading((prev) => ({ ...prev, xlsx: true }));
    logReportDownload('xlsx', reportsToExport);

    try {
      const reviewName = oneReview?.name || "findings";
      const filename = `${reviewName.replace(/\s+/g, "-")}-findings.xlsx`;

      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Findings');
      
      // Define column widths without headers
      worksheet.columns = [
        { key: 'id', width: 8 }, 
        { key: 'severity', width: 15 }, 
        { key: 'description', width: 80 }, 
        { key: 'status', width: 15 }, 
        { key: 'clientResponse', width: 20 }
      ];
      
      // Add DRAFT header row
      const draftRow = worksheet.addRow(['', '', 'DRAFT', '', '']);
      worksheet.mergeCells('C1:E1');
      draftRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' }  // Light gray
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        if (cell.address === 'C1') {
          cell.font = { bold: true, size: 14 };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
      });
      
      // Add the header row manually with the titles in the light blue section
      const headerRow = worksheet.addRow(['ID', 'Severity', 'Description', 'Status', 'Client Response']);
      
      // Style the headers
      headerRow.eachCell((cell, colNumber) => {
        if (colNumber <= 5) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1F4E78' }  // Dark blue
          };
          cell.font = { 
            bold: true, 
            color: { argb: 'FFFFFFFF' }  // White
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        }
      });
      
      // Group reports by file path
      const reportsByFile = {};
      reportsToExport.forEach(report => {
        const filePath = report.filePath || "Unknown File";
        if (!reportsByFile[filePath]) {
          reportsByFile[filePath] = [];
        }
        reportsByFile[filePath].push(report);
      });
      
      // Counter for issue IDs
      let issueCounter = 1;
      
      // Add data for each file
      Object.entries(reportsByFile).forEach(([filePath, reports]) => {
        // Extract just the filename from the path (optional - remove if you want full path)
        const filePathParts = filePath.split('/');
        const fileName = filePathParts[filePathParts.length - 1];
        
        // Add file path row - centered in the cell
        const fileRow = worksheet.addRow([fileName, '', '', '', '']);
        
        // Merge the filename cell across all columns for better visibility
        worksheet.mergeCells(`A${fileRow.number}:E${fileRow.number}`);
        
        // Style the file header row
        fileRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF0F243E' }  // Very dark blue/navy
          };
          cell.font = {
            bold: true,
            color: { argb: 'FFFFFFFF' }  // White
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
        
        // Set row height for better visibility
        fileRow.height = 25;
        
        // Add reports for this file
        reports.forEach(report => {
          // Create ID in format C01, C02, etc.
          const id = `C${String(issueCounter).padStart(2, '0')}`;
          
          // Format description to include all relevant details
          const description = [
            report.title,
            "",
            report.description || "",
            "",
            "As a result, the contract may end up without any control, and admin access to it will be lost.",
            "",
            "Recommendation:",
            report.recommendation || ""
          ].join("\n");
          
          // Add row for this report
          const row = worksheet.addRow([
            id, 
            report.severity?.toUpperCase() || "MEDIUM", 
            description,
            "Open",
            ""
          ]);
          
          // Style the cells
          row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' }; // ID
          
          row.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' }; // Severity
          row.getCell(2).font = { bold: true };
          
          row.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' }; // Status
          
          // Wrap text in description column
          row.getCell(3).alignment = { wrapText: true, vertical: 'top' };
          
          issueCounter++;
        });
      });
      
      // Generate Excel file and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, filename);

      showAlert({
        message: `Excel file "${filename}" is being downloaded.`,
        severity: "success",
        title: "XLSX Download"
    });
    } catch (error) {
      console.error("Error generating XLSX:", error);
      showAlert({
        message: error.message || "Could not generate Excel file.",
        severity: "error",
        title: "XLSX Error"
      });
    } finally {
      setIsDownloading((prev) => ({ ...prev, xlsx: false }));
    }
  }

  // CSV download handler
  const handleDownloadAsCsv = (reportsToExport = filteredReports) => {
    if (isDownloading.csv || !oneReview?._id) return

    setIsDownloading((prev) => ({ ...prev, csv: true }))
    logReportDownload('csv', reportsToExport)

    try {
      const reviewName = oneReview?.name || "findings"
      const filename = `${reviewName.replace(/\s+/g, "-")}-findings.csv`

      // Prepare CSV header
      const header = [
        "Title",
        "Severity",
        "Categories",
        "Description",
        "Potential Risk",
        "Recommendation",
        "File Path",
        "Line Number",
        "Code Snippet",
      ]

      // Prepare CSV rows
      const rows = reportsToExport.map((report) => {
        const categories = Array.isArray(report.categories)
          ? report.categories.map((c) => findCategoryLabel(c).label).join("; ")
          : report.category
          ? findCategoryLabel(report.category).label
          : ""

        // Escape double quotes and wrap fields in quotes
        const escape = (str) =>
          (`${str || ""}`).replace(/"/g, '""')

        return [
          `"${escape(report.title)}"`,
          `"${escape(report.severity?.toUpperCase() || "MEDIUM")}"`,
          `"${escape(categories)}"`,
          `"${escape(report.description)}"`,
          `"${escape(report.potentialRisk)}"`,
          `"${escape(report.recommendation)}"`,
          `"${escape(report.filePath)}"`,
          `"${escape(report.lineNumber)}"`,
          `"${escape(report.codeSnippet?.trim())}"`,
        ].join(",")
      })

      // Combine header and rows
      const csvContent = [header.join(","), ...rows].join("\r\n")

      // Create a Blob and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      saveAs(blob, filename)

      showAlert({
        message: `CSV file "${filename}" is being downloaded.`,
        severity: "success",
        title: "CSV Download",
      })
    } catch (error) {
      console.error("Error generating CSV:", error)
      showAlert({
        message: error.message || "Could not generate CSV file.",
        severity: "error",
        title: "CSV Error",
      })
    } finally {
      setIsDownloading((prev) => ({ ...prev, csv: false }))
    }
  }

  // API handler
  const handleApiClick = () => {
    if (isDownloading.api || !oneReview?._id) return

    setIsDownloading((prev) => ({ ...prev, api: true }))

    try {
      const endpoint = getEndPoint()
      // Generate the API endpoint
      const apiEndpoint = `${endpoint}/compilation/run_tests`

      // In a real app, you'd fetch this token from your auth service
      // For now we'll generate a sample one
      const accessToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(
        JSON.stringify({
          reviewId: oneReview._id,
          exp: Math.floor(Date.now() / 1000) + 86400, // 24 hours from now
        }),
      )}.sample_signature`

      // Store API info for the dialog
      setApiInfo({
        endpoint: apiEndpoint,
        token: accessToken,
      })

      // Open the dialog
      setApiDialogOpen(true)
    } catch (error) {
      console.error("Error handling API info:", error)
      showAlert({
        message: error.message || "Could not provide API information.",
        severity: "error",
        title: "API Error",
      })
    } finally {
      setIsDownloading((prev) => ({ ...prev, api: false }))
    }
  }

  // Add this function to handle copying text to clipboard
  const handleCopyText = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showAlert({
          message: "Copied to clipboard!",
          severity: "success",
          title: "Success",
        })
      })
      .catch((err) => {
        console.error("Failed to copy text:", err)
        showAlert({
          message:
            "Failed to copy text. Please try selecting and copying manually.",
          severity: "error",
          title: "Copy Failed",
        })
      })
  }

  // DOCX download handler with fixed save method
const handleDownloadAsDocx = async (reportsToExport = filteredReports) => {
  if (isDownloading.docx || !oneReview?._id) return;
  
  setIsDownloading((prev) => ({ ...prev, docx: true }));
  logReportDownload('docx', reportsToExport);
  
  try {
    const reviewName = oneReview?.name || "findings";
    const filename = `${reviewName.replace(/\s+/g, "-")}-findings.docx`;
    
    // Create document sections for each report
    const reportSections = reportsToExport.flatMap(report => {
      // Helper function to get severity color
      const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
          case 'critical': return "FF0000";
          case 'high': return "F69E08";
          case 'medium': return "D1B900";
          case 'low': return "0BC543";
          case 'informational': return "757575";
          default: return "000000";
        }
      };
      
      // Get categories formatted as string
      const categories = Array.isArray(report.categories)
        ? report.categories.map(c => findCategoryLabel(c).label).join(", ")
        : report.category ? findCategoryLabel(report.category).label : "";
      
      const hasSnippet = !!(report.codeSnippet && report.codeSnippet.trim())
      const hasDescription = !!(report.description && String(report.description).trim())
      const hasRisk = !!(report.potentialRisk && String(report.potentialRisk).trim())
      const hasRecommendation = !!(report.recommendation && String(report.recommendation).trim())

      const descriptionParagraphs = hasDescription
        ? [
            new Paragraph({
              text: "Description",
              heading: HeadingLevel.HEADING_4,
              spacing: { before: 80, after: 40 },
            }),
            new Paragraph({
              text: report.description,
              spacing: { after: 80 },
            }),
          ]
        : []

      const riskParagraphs = hasRisk
        ? [
            new Paragraph({
              text: "Potential Risk",
              heading: HeadingLevel.HEADING_4,
              spacing: { before: 80, after: 40 },
            }),
            new Paragraph({
              text: report.potentialRisk,
              spacing: { after: 80 },
            }),
          ]
        : []

      const recommendationParagraphs = hasRecommendation
        ? [
            new Paragraph({
              text: "Recommendation",
              heading: HeadingLevel.HEADING_4,
              spacing: { before: 80, after: 40 },
            }),
            new Paragraph({
              text: report.recommendation,
              spacing: { after: 80 },
            }),
          ]
        : []

      const codeSnippetParagraphs = hasSnippet
        ? [
            new Paragraph({
              text: "Code Snippet",
              heading: HeadingLevel.HEADING_4,
              spacing: { before: 80, after: 40 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `${report.filePath}${
                    report.lineNumber ? ` line ${report.lineNumber}` : ""
                  }:`,
                  bold: true,
                }),
              ],
              spacing: { after: 40 },
            }),
            new Paragraph({
              text: report.codeSnippet.trim(),
              spacing: { before: 40, after: 120 },
              border: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
              },
              shading: { type: ShadingType.SOLID, color: "F5F5F5" },
              style: "codeBlock",
            }),
          ]
        : []

      return [
        new Paragraph({
          text: report.title,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 160, after: 60 }
        }),
        
        // Severity and categories
        new Paragraph({
          children: [
            new TextRun({
              text: `Severity: ${report.severity?.toUpperCase() || "MEDIUM"}`,
              bold: true,
              color: getSeverityColor(report.severity)
            }),
            new TextRun({ text: "   " }),
            new TextRun({
              text: categories ? `Categories: ${categories}` : "",
              bold: true,
              color: "1A00FF"
            })
          ],
          spacing: { before: 40, after: 60 }
        }),
        
        // Description (only if non-empty)
        ...descriptionParagraphs,
        
        // Potential Risk (only if non-empty)
        ...riskParagraphs,
        
        // Recommendation (only if non-empty)
        ...recommendationParagraphs,
        
        // Code Snippet (only if non-empty)
        ...codeSnippetParagraphs,
        
        // Spacer between reports
        new Paragraph({ text: "", spacing: { after: 120 } })
      ];
    });
    
    // Create the document
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: "Findings and Recommendations",
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 80 }
          }),
          new Paragraph({
            text: `Review: ${oneReview?.name || "Smart Contract Review"}`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 80, after: 160 }
          }),
          ...reportSections
        ]
      }],
      styles: {
        paragraphStyles: [
          {
            id: "codeBlock",
            name: "Code Block",
            basedOn: "Normal",
            run: {
              font: "Courier New",
              size: 18
            },
            paragraph: {
              spacing: { before: 80, after: 80 },
              indent: { left: 360 }
            }
          }
        ]
      }
    });
    
    // Generate and save the document using Packer (this is the fix)
    const buffer = await Packer.toBuffer(doc);
    // Convert Node.js Buffer to Uint8Array for Blob compatibility
    const uint8Array = new Uint8Array(buffer);
    const blob = new Blob([uint8Array], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    saveAs(blob, filename);
    
    showAlert({
      message: `DOCX file "${filename}" is being downloaded.`,
      severity: "success",
      title: "DOCX Download"
    });
  } catch (error) {
    console.error("Error generating DOCX:", error);
    showAlert({
      message: error.message || "Could not generate DOCX file.",
      severity: "error",
      title: "DOCX Error"
    });
  } finally {
    setIsDownloading((prev) => ({ ...prev, docx: false }));
  }
};

  // Map a format string to its download handler. Keeps `triggerDownload`/
  // `runDownload` free of long switch statements.
  const downloadHandlers = {
    pdf: handleDownloadAsPdf,
    docx: handleDownloadAsDocx,
    csv: handleDownloadAsCsv,
    xlsx: handleDownloadAsXlsx,
    json: handleDownloadAsJson,
  }

  // Build the actual list of reports to export based on the simulation
  // inclusion choice. Security findings are always exported. Exports use the
  // raw (unfiltered) lists so the downloaded report matches the totals shown
  // in the top stat cards / tab labels, independent of any sidebar filters
  // the user may have toggled while browsing.
  const buildExportReports = (includeSimulations) => {
    const securityForExport = sortBySeverity(rawSecurityReports)
    const simulationForExport = sortBySeverity(rawSimulationReports)
    if (includeSimulations) return [...securityForExport, ...simulationForExport]
    return securityForExport
  }

  // Run a download for the chosen format with the chosen inclusion preference.
  const runDownload = (format, includeSimulations) => {
    const handler = downloadHandlers[format]
    if (!handler) return
    handler(buildExportReports(includeSimulations))
  }

  // Entry point used by every download link in the toolbar. When simulation
  // findings exist we first ask the user whether to include them; otherwise we
  // skip the modal and download the Security Findings tab directly.
  const triggerDownload = (format) => {
    if (!downloadHandlers[format]) return
    if (hasSimulationReports && rawSimulationReports.length > 0) {
      setDownloadModal({ open: true, format, includeSimulations: true })
    } else {
      runDownload(format, false)
    }
  }

  const handleConfirmDownload = () => {
    const { format, includeSimulations } = downloadModal
    setDownloadModal({ open: false, format: null, includeSimulations: true })
    if (format) runDownload(format, includeSimulations)
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Box className={classes.filterHeader}>
          {!isDesktop ? (
            // Show IconButton with children if not desktop
            <>
              <IconButton onClick={toggleFiltersVisibility}>
                <FilterListIcon />
                <Typography variant="h6" style={{ marginLeft: 8 }}>
                  Filters
                </Typography>
              </IconButton>
              <Button onClick={handleClearAll} color="primary">
                Clear All
              </Button>
            </>
          ) : (
            // Show only the children (no IconButton) if desktop
            <>
              <Box className={classes.filterTitleContainer}>
                <FilterListIcon />
                <Typography variant="h6" style={{ marginLeft: 8 }}>
                  Filters
                </Typography>
              </Box>
              <Button onClick={handleClearAll} color="primary">
                Clear All
              </Button>
            </>
          )}
        </Box>
        {(filtersVisible || isDesktop) && (
          <ReportFilters
            filters={filters}
            checkedFilters={checkedFilters}
            expanded={expanded}
            onFilterChange={handleFilterChange}
            onExpandToggle={handleExpandToggle}
          />
        )}
      </Grid>
      <Grid item xs={12} md={9}>
        <Box className={classes.linkContainer}>
          {/* Only show re-run button if debug flag is enabled */}
          {showReRunButton && (
            <Box
              className={classes.reRunButton}
              onClick={handleReRunTests}
              style={{ opacity: isRunningTests ? 0.7 : 1 }}
            >
              {isRunningTests ? (
                <CircularProgress size={20} style={{ marginRight: 8 }} />
              ) : (
                <RefreshIcon style={{ marginRight: 8 }} />
              )}
              <Typography component="span">Re-run Tests</Typography>
            </Box>
          )}

          {/* If debug flag is disabled, add an empty Box to maintain layout */}
          {!showReRunButton && <Box />}

          {/* Right side: Export links */}
          <Box style={{ display: "flex" }}>
            <Typography
              component="span"
              className={classes.link}
              onClick={handleApiClick}
              style={{ opacity: isDownloading.api ? 0.7 : 1 }}
            >
              {isDownloading.api ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              API
            </Typography>
            <Typography
              component="span"
              className={classes.link}
              onClick={() => triggerDownload('pdf')}
              style={{ opacity: isDownloading.pdf ? 0.7 : 1 }}
            >
              {isDownloading.pdf ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              PDF
            </Typography>
            <Typography
              component="span"
              className={classes.link}
              onClick={() => triggerDownload('docx')}
              style={{ opacity: isDownloading.docx ? 0.7 : 1 }}
            >
              {isDownloading.docx ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              DOCX
            </Typography>
            <Typography
              component="span"
              className={classes.link}
              onClick={() => triggerDownload('xlsx')}
              style={{ opacity: isDownloading.xlsx ? 0.7 : 1 }}
            >
              {isDownloading.xlsx ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              XLSX
            </Typography>
            <Typography
              component="span"
              className={classes.link}
              onClick={() => triggerDownload('csv')}
              style={{ opacity: isDownloading.csv ? 0.7 : 1 }}
            >
              {isDownloading.csv ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              CSV
            </Typography>
            <Typography
              component="span"
              className={classes.link}
              onClick={() => triggerDownload('json')}
              style={{ opacity: isDownloading.json ? 0.7 : 1 }}
            >
              {isDownloading.json ? (
                <CircularProgress
                  size={16}
                  className={classes.downloadingSpinner}
                />
              ) : (
                <GetAppIcon className={classes.downloadIcon} />
              )}
              JSON
            </Typography>
          </Box>
        </Box>

        {/* Tabs: split Security Findings from AI Simulation Test Failures.
            The simulation tab only appears when at least one simulation
            failure finding exists for this review. */}
        {hasSimulationReports && (
          <Box mb={2} borderBottom={1} borderColor="divider">
            <Tabs
              value={activeTab}
              onChange={(_e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="standard"
            >
              <Tab label={`Security Findings (${rawSecurityReports.length})`} />
              <Tab
                label={`Simulation Test Failures (${rawSimulationReports.length})`}
              />
            </Tabs>
          </Box>
        )}

        {displayedReports.length === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ padding: "16px 4px" }}
          >
            {activeTab === 1
              ? "No simulation test failures to display."
              : "No findings to display. Adjust filters or re-run the analysis."}
          </Typography>
        )}

        {displayedReports.map((report, index) => (
          <Paper key={index} className={classes.reportBox} elevation={0}>
            <Typography className={classes.reportTitle}>{report.title}</Typography>
            <Box className={classes.capsuleRow}>
              <span
                className={`${classes.capsule} ${
                  classes[report.severity?.toLowerCase()]
                }`}
              >
                {(() => {
                  // Find the label for this severity
                  const severityFilter = filters.find(
                    (f) => f.id === "severity",
                  )
                  if (!severityFilter) return report.severity

                  const severityItem = severityFilter.children.find(
                    (c) => c.id === report.severity?.toLowerCase(),
                  )
                  return severityItem ? severityItem.label : report.severity
                })()}
              </span>
              {/* Map through all categories in the report */}
              {(() => {
                // Handle both formats: categories array or single category string
                const categoryArray = Array.isArray(report.categories)
                  ? report.categories
                  : report.category
                  ? [report.category]
                  : []

                return categoryArray.map((categoryId, idx) => {
                  const { label } = findCategoryLabel(categoryId)
                  return (
                    <span
                      key={idx}
                      className={`${classes.capsule} ${classes.category}`}
                    >
                      {label}
                    </span>
                  )
                })
              })()}
            </Box>
            {report.description && String(report.description).trim() ? (
              <Typography className={classes.description}>
                {report.description}
              </Typography>
            ) : null}
            {report.potentialRisk && String(report.potentialRisk).trim() ? (
              <>
                <Typography className={classes.sectionHeading}>Potential Risk</Typography>
                <Typography className={classes.recommendation}>
                  {report.potentialRisk}
                </Typography>
              </>
            ) : null}
            {report.recommendation && (
              <Box className={classes.recommendationContainer}>
                <Box className={classes.recommendationHeader}>
                  <EmojiObjectsOutlinedIcon
                    className={classes.recommendationIcon}
                  />
                  <Typography className={classes.recommendationTitle}>
                    Recommendation
                  </Typography>
                </Box>
                <Typography className={classes.recommendationContent}>
                  {report.recommendation}
                </Typography>
              </Box>
            )}
            {report.codeSnippet && report.codeSnippet.trim() ? (
              <>
                <Typography className={classes.sectionHeading}>Code Snippet</Typography>
                <Typography className={classes.recommendation}>
                  <span style={{ fontWeight: "bolder", marginBottom: 5 }}>
                    {report.filePath}
                    {report.lineNumber ? ` line ${report.lineNumber}` : ""}:&nbsp;
                  </span>
                  &nbsp;
                  <span
                    style={{
                      padding: 5,
                      background: "#dddd",
                      fontFamily: "monospace",
                      fontWeight: "bolder",
                    }}
                  >
                    {report.codeSnippet.trim()}
                  </span>
                </Typography>
              </>
            ) : null}
          </Paper>
        ))}
      </Grid>

      {/* Download options modal: only opened when simulation findings exist,
          asks the user whether to include them alongside the main security
          findings in the exported file. */}
      <CustomModal
        showModal={downloadModal.open}
        size="medium"
        title="Export options"
        onCloseModal={() =>
          setDownloadModal({ open: false, format: null, includeSimulations: true })
        }
        cancelBtnText="Cancel"
        submitBtnText="Download"
        onSubmit={handleConfirmDownload}
      >
        <Typography variant="body2" style={{ marginBottom: 12 }}>
          Choose what to include in the
          {downloadModal.format ? ` ${downloadModal.format.toUpperCase()}` : ""}
          {" "}export. Security findings are always included.
        </Typography>
        <CustomCheckbox
          label={`Include simulation test failures (${rawSimulationReports.length})`}
          checked={downloadModal.includeSimulations}
          onChange={(checked) =>
            setDownloadModal((prev) => ({ ...prev, includeSimulations: checked }))
          }
        />
      </CustomModal>

      {/* API Modal */}
      <CustomModal
        showModal={apiDialogOpen}
        size="large"
        title="API Access Information"
        onCloseModal={() => setApiDialogOpen(false)}
        cancelBtnText="Close"
      >
        <Typography variant="h5" gutterBottom style={{ color: "#1976d2", fontWeight: "bold" }}>
          Unified Test Runner API
        </Typography>
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
          Complete smart contract analysis in a single API call - includes compilation, analysis, AI simulations, testing, and reporting.
        </Typography>
        
        {/* Endpoint Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32" }}>
            🌐 API Endpoint
          </Typography>
          <Box display="flex" alignItems="center" mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              value={`${apiInfo.endpoint.replace('/compilation/run_tests', '/compilation/test_runner')}`}
              InputProps={{
                readOnly: true,
                style: { fontFamily: "monospace", fontSize: "0.9rem" }
              }}
            />
            <IconButton
              color="primary"
              onClick={() => handleCopyText(`${apiInfo.endpoint.replace('/compilation/run_tests', '/compilation/test_runner')}`)}
              style={{ marginLeft: 8 }}
            >
              <FileCopyIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Authentication Section */}
        {/* <Box mb={3}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32" }}>
            🔑 Authentication Token
          </Typography>
          <Box display="flex" alignItems="center" mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              value={apiInfo.token}
              InputProps={{
                readOnly: true,
                style: { fontFamily: "monospace", fontSize: "0.9rem" }
              }}
            />
            <IconButton
              color="primary"
              onClick={() => handleCopyText(apiInfo.token)}
              style={{ marginLeft: 8 }}
            >
              <FileCopyIcon />
            </IconButton>
          </Box>
        </Box> */}

        {/* Request Example Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32" }}>
            📤 Request Example
          </Typography>
          <Box p={3} border="1px solid #e0e0e0" borderRadius={4} bgcolor="#fafafa">
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold" }}>
              Method: POST
            </Typography>
            
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold" }}>
              Request Body:
            </Typography>
            <Box bgcolor="#f5f5f5" p={2} borderRadius={4} style={{ fontFamily: "monospace", fontSize: "0.8rem", overflowX: "auto" }}>
              {`{
  "compilationPayload": {
    "getCompiledPayload": {
      "compilerVersion": "v0.8.19+commit.7dd6d404",
      "compilerSettings": {
        "optimizer": { "enabled": true, "runs": 200 },
        "viaIR": false
      },
      "files": [
        {
          "path": "main.sol",
          "content": "pragma solidity ^0.8.0;\\n\\ncontract MyContract {\\n  uint256 public value;\\n\\n  function setValue(uint256 _value) public {\\n    value = _value;\\n  }\\n}"
        }
      ],
      "mainContract": "MyContract",
      "mainFile": "main.sol"
    },
    "submitTestDetailsPayload": {
      "compilerVersion": "v0.8.19+commit.7dd6d404",
      "mainContract": "MyContract",
      "mainFile": "main.sol",
      "compilerSettings": {
        "optimizer": { "enabled": true, "runs": 200 }
      },
      "files": [
        {
          "name": "main.sol",
          "content": "pragma solidity ^0.8.0;\\n\\ncontract MyContract {\\n  uint256 public value;\\n\\n  function setValue(uint256 _value) public {\\n    value = _value;\\n  }\\n}"
        }
      ],
      "architectureDocuments": [],
      "specificationsDocuments": [
        {
          "name": "requirements.txt",
          "content": "Smart contract should allow setting and getting a value safely"
        }
      ]
    }
  }
}`}
            </Box>
          </Box>
        </Box>

        {/* Response Example Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32" }}>
            📥 Response Example
          </Typography>
          <Box p={3} border="1px solid #e0e0e0" borderRadius={4} bgcolor="#fafafa">
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold" }}>
              Success Response (200 OK):
            </Typography>
            <Box bgcolor="#f5f5f5" p={2} borderRadius={4} style={{ fontFamily: "monospace", fontSize: "0.8rem", overflowX: "auto" }}>
              {`{
  "success": true,
  "reviewId": "507f1f77bcf86cd799439011",
  "abi": [
    {
      "inputs": [],
      "name": "value",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    }
  ],
  "bytecode": "0x608060405234801561001057600080fd5b50...",
  "analysisResult": {
    "success": true,
    "issues": [
      {
        "severity": "Low",
        "title": "Missing input validation",
        "description": "Function setValue does not validate input parameters"
      }
    ],
    "totalIssues": 1
  },
  "aiSimulationResult": {
    "success": true,
    "simulations": [
      {
        "testName": "setValue_ValidInput",
        "status": "PASSED",
        "description": "Setting value with valid input"
      }
    ],
    "totalSimulations": 5,
    "passedTests": 4,
    "failedTests": 1
  },
  "testResults": {
    "success": true,
    "results": [
      {
        "testCase": "Basic functionality",
        "status": "PASSED",
        "gasUsed": "43254"
      }
    ]
  },
  "simulationResults": {
    "success": true,
    "totalSimulations": 5,
    "executedSimulations": 5
  },
  "reportResults": {
    "success": true,
    "totalReports": 12,
    "criticalIssues": 0,
    "highIssues": 2,
    "mediumIssues": 5,
    "lowIssues": 5
  },
  "message": "All compilation steps completed successfully"
}`}
            </Box>
          </Box>
        </Box>

        {/* cURL Example Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32" }}>
            💻 cURL Command
          </Typography>
          <Box p={3} border="1px solid #e0e0e0" borderRadius={4} bgcolor="#fafafa">
            <Typography variant="body2" color="textSecondary">
              Copy and paste this command into your terminal:
            </Typography>
            <Box
              bgcolor="#263238"
              color="white"
              p={2}
              borderRadius={4}
              style={{ fontFamily: "monospace", overflowX: "auto", fontSize: "0.8rem" }}
            >
              {`curl -X POST "${apiInfo.endpoint.replace('/compilation/run_tests', '/compilation/test_runner')}" \\
  -H "Authorization: Bearer ${apiInfo.token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "compilationPayload": {
      "getCompiledPayload": {
        "compilerVersion": "v0.8.19+commit.7dd6d404",
        "compilerSettings": {"optimizer": {"enabled": true, "runs": 200}},
        "files": [{"path": "main.sol", "content": "pragma solidity ^0.8.0; contract MyContract { uint256 public value; function setValue(uint256 _value) public { value = _value; } }"}],
        "mainContract": "MyContract",
        "mainFile": "main.sol"
      },
      "submitTestDetailsPayload": {
        "compilerVersion": "v0.8.19+commit.7dd6d404",
        "mainContract": "MyContract",
        "mainFile": "main.sol",
        "files": [{"name": "main.sol", "content": "pragma solidity ^0.8.0; contract MyContract { uint256 public value; function setValue(uint256 _value) public { value = _value; } }"}]
      }
    }
  }'`}
            </Box>
          </Box>
        </Box>

        {/* Key Features Section */}
        <Box mb={3} p={3} bgcolor="#e8f5e8" borderRadius={4}>
          <Typography variant="h6" gutterBottom style={{ color: "#2e7d32", fontWeight: "bold" }}>
            ✨ What This API Does
          </Typography>
          <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#1565c0" }}>
                🔧 Compilation
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Compiles Solidity contracts with specified settings
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#1565c0" }}>
                🔍 Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Performs security analysis and vulnerability detection
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#1565c0" }}>
                🤖 AI Simulations
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Generates and runs AI-powered test simulations
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#1565c0" }}>
                📊 Reporting
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Creates comprehensive security and analysis reports
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Guidelines Section */}
        <Box p={3} bgcolor="#e3f2fd" borderRadius={4}>
          <Typography variant="h6" gutterBottom style={{ fontWeight: "bold", color: "#0d47a1" }}>
            📋 Integration Guidelines
          </Typography>
          <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                Rate Limits:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                10 requests/minute (development)
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                File Size Limits:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                10MB per file, 50MB total
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                Response Time:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                30-120 seconds (depending on complexity)
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                Support:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Contact team for production access
              </Typography>
            </Box>
          </Box>
        </Box>
      </CustomModal>
    </Grid>
  )
}
